"use client";

import { AnimatePresence, motion } from "framer-motion";
import Icon, { IconNameExtended } from "@/components/library/Icon";
import { Button as IconButton } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { DaysOfWeek } from "@/enums/settings";
import { DAY_LABEL_KEYS, type Slot, type TFunction } from "@/utils/onboarding/businessHoursUtils";

export interface TimeOption {
  value: string;
  label: string;
}

interface TimeSlotRowOptions {
  label?: string;
  showLabelColumn?: boolean;
  showActionsColumn?: boolean;
  onAdd?: () => void;
  onCopy?: () => void;
  onPaste?: () => void;
  onDone?: () => void;
  onRemove?: () => void;
  disabled?: boolean;
}

export function TimeSlotRow({
  start,
  end,
  onStartChange,
  onEndChange,
  t,
  timeOptions,
  options,
}: Readonly<{
  start: string;
  end: string;
  onStartChange: (v: string) => void;
  onEndChange: (v: string) => void;
  t: TFunction;
  timeOptions: TimeOption[];
  options?: TimeSlotRowOptions;
}>) {
  const {
    label,
    showLabelColumn,
    showActionsColumn,
    onAdd,
    onCopy,
    onPaste,
    onDone,
    onRemove,
    disabled,
  } = options || {};
  const hasActions = onAdd || onCopy || onPaste || onDone || onRemove;
  const hasLabelCol = label || showLabelColumn;
  const hasActionsCol = hasActions || showActionsColumn;

  const actionDefs = [
    {
      key: "add",
      handler: onAdd,
      icon: "plus" as IconNameExtended,
      title: t("businessHours.addTimeSlot"),
      className: "h-7 w-7",
    },
    {
      key: "copy",
      handler: onCopy,
      icon: "copy" as IconNameExtended,
      title: t("businessHours.copySchedule"),
      className: "h-7 w-7 hover:text-primary !duration-0",
    },
    {
      key: "paste",
      handler: onPaste,
      icon: "paste" as IconNameExtended,
      title: t("businessHours.pasteSchedule"),
      className: "h-7 w-7 text-primary hover:text-primary !duration-0",
    },
    {
      key: "done",
      handler: onDone,
      icon: "xmark" as IconNameExtended,
      title: t("businessHours.doneCopy"),
      className: "h-7 w-7 hover:text-destructive !duration-0",
    },
    {
      key: "remove",
      handler: onRemove,
      icon: "trash" as IconNameExtended,
      title: t("businessHours.removeTimeSlot"),
      className: "h-7 w-7 hover:text-destructive !duration-0",
    },
  ].filter((btn): btn is typeof btn & { handler: () => void } => Boolean(btn.handler));

  return (
    <div
      className="grid items-center gap-x-2"
      style={{
        gridTemplateColumns: [
          hasLabelCol ? "2.5rem" : null,
          "1fr",
          "auto",
          "1fr",
          hasActionsCol ? "auto" : null,
        ]
          .filter(Boolean)
          .join(" "),
      }}
    >
      {hasLabelCol && <span className="text-sm font-medium text-foreground">{label ?? ""}</span>}
      <Combobox
        options={timeOptions}
        value={start}
        onChange={onStartChange}
        triggerClassName="w-full"
        disabled={disabled}
      />
      <span className="text-sm text-muted-foreground text-center">
        {t("businessHours.time.to")}
      </span>
      <Combobox
        options={timeOptions}
        value={end}
        onChange={onEndChange}
        triggerClassName="w-full"
        disabled={disabled}
        defaultScrollToValue={start}
      />
      {hasActionsCol && (
        <div className="flex items-center justify-end gap-0.5 min-w-[3.75rem]">
          <AnimatePresence mode="popLayout" initial={false}>
            {actionDefs.map(({ key, handler, icon, title, className: btnClass }) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={{ duration: 0.15 }}
              >
                <IconButton
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={btnClass}
                  onClick={handler}
                  title={title}
                >
                  <Icon icon={icon} className="w-3.5 h-3.5" />
                </IconButton>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

export function buildSlotOptions(
  slotIndex: number,
  day: DaysOfWeek,
  isInCopyMode: boolean,
  isSource: boolean,
  isTarget: boolean,
  t: TFunction,
  callbacks: {
    addTimeSlot: (day: DaysOfWeek) => void;
    enterCopyMode: (day: DaysOfWeek) => void;
    removeTimeSlot: (day: DaysOfWeek, index: number) => void;
    exitCopyMode: () => void;
    pasteToDay: (day: DaysOfWeek) => void;
  }
) {
  const isFirstSlot = slotIndex === 0;
  const canAct = !isInCopyMode;

  let onAdd: (() => void) | undefined;
  let onCopy: (() => void) | undefined;
  let onRemove: (() => void) | undefined;
  let onDone: (() => void) | undefined;
  let onPaste: (() => void) | undefined;

  if (canAct && isFirstSlot) {
    onAdd = () => callbacks.addTimeSlot(day);
    onCopy = () => callbacks.enterCopyMode(day);
  }
  if (canAct && !isFirstSlot) {
    onRemove = () => callbacks.removeTimeSlot(day, slotIndex);
  }
  if (isSource && isFirstSlot) {
    onDone = callbacks.exitCopyMode;
  }
  if (isTarget && isFirstSlot) {
    onPaste = () => callbacks.pasteToDay(day);
  }

  return {
    label: isFirstSlot ? t(DAY_LABEL_KEYS[day]) : undefined,
    showLabelColumn: !isFirstSlot,
    showActionsColumn: !isFirstSlot,
    onAdd,
    onCopy,
    onRemove,
    onDone,
    onPaste,
    disabled: false,
  };
}

/** Renders a single slot row for a day, with animated wrapper for extra (non-first) slots */
export function AnimatedSlotRow({
  slot,
  slotIndex,
  day,
  isSource,
  isTarget,
  isInCopyMode,
  t,
  timeOptions,
  callbacks,
}: {
  slot: Slot;
  slotIndex: number;
  day: DaysOfWeek;
  isSource: boolean;
  isTarget: boolean;
  isInCopyMode: boolean;
  t: TFunction;
  timeOptions: TimeOption[];
  callbacks: {
    updateDaySlot: (
      day: DaysOfWeek,
      slotIndex: number,
      field: "start" | "end",
      value: string
    ) => void;
    addTimeSlot: (day: DaysOfWeek) => void;
    enterCopyMode: (day: DaysOfWeek) => void;
    removeTimeSlot: (day: DaysOfWeek, index: number) => void;
    exitCopyMode: () => void;
    pasteToDay: (day: DaysOfWeek) => void;
  };
}) {
  const options = buildSlotOptions(slotIndex, day, isInCopyMode, isSource, isTarget, t, callbacks);

  const row = (
    <TimeSlotRow
      start={slot.start}
      end={slot.end}
      onStartChange={(v) => callbacks.updateDaySlot(day, slotIndex, "start", v)}
      onEndChange={(v) => callbacks.updateDaySlot(day, slotIndex, "end", v)}
      t={t}
      timeOptions={timeOptions}
      options={options}
    />
  );

  if (slotIndex > 0) {
    return (
      <motion.div
        key={slot._id}
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: "auto", opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
        className="overflow-hidden"
      >
        {row}
      </motion.div>
    );
  }

  return <div key={slot._id}>{row}</div>;
}
